import { doc, getDoc } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { firestore } from "../firebase";
import { PublicListing, PublicListingCollection } from "../types/PublicListing";
import {
  CountryStats,
  NormalizedStats,
  WithAmountsStats,
} from "../types/PublicListingStats";

interface PublicListingStatsResult {
  normalized: NormalizedStats | null;
  withAmounts: WithAmountsStats | null;
  countries: CountryStats | null;
  isLoading: boolean;
}

export function usePublicListingStats(
  listing: PublicListing | null,
): PublicListingStatsResult {
  const [normalized, setNormalized] = useState<NormalizedStats | null>(null);
  const [withAmounts, setWithAmounts] = useState<WithAmountsStats | null>(null);
  const [countries, setCountries] = useState<CountryStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!listing) {
      setIsLoading(false);
      return;
    }

    const projectId = listing.id;
    const statsBase = `${PublicListingCollection}/${projectId}/monthly-stats`;

    const fetches: Promise<void>[] = [];

    // Always fetch normalized (trendline shapes)
    const showRevenue = listing.revenueDisplay !== "hidden";
    const showSubscriptions = listing.subscriptionsDisplay !== "hidden";

    if (showRevenue || showSubscriptions) {
      fetches.push(
        getDoc(doc(firestore, statsBase, "normalized")).then((snap) => {
          if (snap.exists()) {
            setNormalized(snap.data() as NormalizedStats);
          }
        }),
      );
    }

    // Fetch with-amounts only if at least one display setting requires it
    const needsAmounts =
      listing.revenueDisplay === "trendline-with-amounts" ||
      listing.subscriptionsDisplay === "trendline-with-amounts";

    if (needsAmounts) {
      fetches.push(
        getDoc(doc(firestore, statsBase, "with-amounts")).then((snap) => {
          if (snap.exists()) {
            setWithAmounts(snap.data() as WithAmountsStats);
          }
        }),
      );
    }

    // Fetch countries if not hidden
    if (listing.countryDisplay !== "hidden") {
      fetches.push(
        getDoc(doc(firestore, statsBase, "countries")).then((snap) => {
          if (snap.exists()) {
            setCountries(snap.data() as CountryStats);
          }
        }),
      );
    }

    if (fetches.length === 0) {
      setIsLoading(false);
      return;
    }

    Promise.all(fetches)
      .catch((err) => {
        console.error("Failed to load public listing stats:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [listing]);

  return useMemo(
    () => ({ normalized, withAmounts, countries, isLoading }),
    [normalized, withAmounts, countries, isLoading],
  );
}
